import { defaults } from 'lodash';
import {
  CollaborativeApps,
  APP_DEFAULT_WIDTH,
  APP_DEFAULT_HEIGHT,
  APPS_MIN_WIDTH,
  APPS_MAX_WIDTH,
  APPS_MIN_HEIGHT,
  APPS_MAX_HEIGHT,
} from '../../iframe/additional-apps.utils';
import { FrameItem } from '../../../../models/session';
import { BoardItemConfig, CreationContext } from './board-item-configurations';

export class WebViewerConfigurations extends BoardItemConfig {
  appInstanceId: string;
  // To enable the support of graphics library in the created VM instance, should be used carefully as that is expensive (once set when creating an instance, can not be changed)
  webGLEnabled: boolean;
  title: string;
  initialZoom: number;
  // Sets the locale of the browser created, helps with unblocking certain sites and regional-based content
  country: string;
  // Decides wither we have the ublock extension added to the created VM (once set when creating an instance, can not be changed)
  adBlockEnabled: boolean;
  startUrl?: string;
  isPrivate: boolean;
  interactionsLocked: boolean;
  available: boolean;
  hasAvailabilityNotification: boolean;
  hyperbeamSessionId?: string;
  // Used to connect to the web viewer VM instance and should be provided to the SDK
  embedUrl?: string;
  lastVisitedUrl?: string;
  isPortalSession: boolean;
  // Per-session Portal MCP token — carried through so it round-trips onto
  // browser_state. The FE never uses it itself.
  remoteControlToken?: string;

  static defaultBoardItemConfigurations: Partial<BoardItemConfig> = {
    width: APP_DEFAULT_WIDTH,
    height: APP_DEFAULT_HEIGHT,
    minWidth: APPS_MIN_WIDTH,
    maxWidth: APPS_MAX_WIDTH,
    minHeight: APPS_MIN_HEIGHT,
    maxHeight: APPS_MAX_HEIGHT,
  };

  constructor(
    configurations: Partial<WebViewerConfigurations> & Partial<BoardItemConfig>,
    globalCreationParams: CreationContext,
  ) {
    super(
      {
        ...WebViewerConfigurations.defaultBoardItemConfigurations,
        ...configurations,
      },
      globalCreationParams,
    );

    const app =
      globalCreationParams.appConfigurationService.getAppByUid(
        configurations.appInstanceId ?? CollaborativeApps.WEBVIEWER,
      ) ?? // this case happens if an item from that map was removed without considering existing boards data
      globalCreationParams.appConfigurationService.getAppByUid(CollaborativeApps.WEBVIEWER)!;

    const defaultWebViewerConfigurations = {
      appInstanceId: CollaborativeApps.WEBVIEWER,
      title: app.title,
      isPrivate: false,
      webGLEnabled: false,
      initialZoom: 1,
      country: 'US',
      adBlockEnabled: false,
      interactionsLocked: false,
      available: true,
      hasAvailabilityNotification: false,
      isPortalSession: false,
    };

    const webViewerConfigurations = defaults(configurations, defaultWebViewerConfigurations);

    this.appInstanceId = webViewerConfigurations.appInstanceId;
    this.webGLEnabled = webViewerConfigurations.webGLEnabled;
    this.title = webViewerConfigurations.title;
    this.initialZoom = webViewerConfigurations.initialZoom;
    this.country = webViewerConfigurations.country;
    this.adBlockEnabled = webViewerConfigurations.adBlockEnabled;
    this.startUrl = webViewerConfigurations.startUrl;
    this.isPrivate = webViewerConfigurations.isPrivate;
    this.interactionsLocked = webViewerConfigurations.interactionsLocked;
    this.available = webViewerConfigurations.available;
    this.hasAvailabilityNotification = webViewerConfigurations.hasAvailabilityNotification;
    this.hyperbeamSessionId = webViewerConfigurations.hyperbeamSessionId;
    this.embedUrl = webViewerConfigurations.embedUrl;
    this.lastVisitedUrl = webViewerConfigurations.lastVisitedUrl;
    this.isPortalSession = webViewerConfigurations.isPortalSession;
    this.remoteControlToken = webViewerConfigurations.remoteControlToken;
  }

  static fromYCanvasItemObject(
    frameItem: FrameItem,
    globalCreationsParams: CreationContext,
  ): WebViewerConfigurations {
    const configurations = BoardItemConfig.fromYCanvasItemObject(
      frameItem,
      globalCreationsParams,
      WebViewerConfigurations.defaultBoardItemConfigurations,
    );
    return new WebViewerConfigurations(
      {
        ...configurations,

        appInstanceId: frameItem.browser_state?.type as CollaborativeApps,
        title: frameItem.browser_state?.title,
        startUrl: frameItem.browser_state?.defaultUrl,
        webGLEnabled: frameItem.browser_state?.webgl,
        initialZoom: frameItem.browser_state?.initialZoom,
        country: frameItem.browser_state?.country,
        adBlockEnabled: frameItem.browser_state?.adblock,
        interactionsLocked: frameItem.interactionsLocked,
        available: frameItem.available,
        hasAvailabilityNotification: frameItem.hasAvailabilityNotification,
        isPrivate: frameItem.isPrivate,
        hyperbeamSessionId: frameItem.browser_state?.hbSessionId,
        embedUrl: frameItem.browser_state?.url,
        lastVisitedUrl: frameItem.browser_state?.lastVisitedUrl,
        isPortalSession: frameItem.browser_state?.isPortalSession,
        remoteControlToken: frameItem.browser_state?.remoteControlToken,
      },
      globalCreationsParams,
    );
  }
}
